import { Component, OnInit, HostListener, Input, Output, EventEmitter, inject, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { filter } from 'rxjs/operators';
import { navItems } from './sidebar-data';
import { NavItem } from './nav-item/nav-item';
import { HasRoleDirective } from "@core/guards/has-role.directive";
import { SessionService } from '../../../core';
import { MI_PERFIL_ACCESS_ROLES, UserRole } from '@core/constants/roles.constants';
import { TranslatePipe } from '@ngx-translate/core';
import { ParametroGlobalContextService } from '@features/configuracion-global/parametros-globales/services/parametro-global-context.service';
import { PermissionService } from '@core/services/permission.service';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, HasRoleDirective, TranslatePipe],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  animations: [
    // Animación de fade in
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-in', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('200ms ease-out', style({ opacity: 0 }))
      ])
    ]),
    // Animación de slide down para submenús
    trigger('slideDown', [
      transition(':enter', [
        style({
          opacity: 0,
          height: 0,
          transform: 'translateY(-10px)'
        }),
        animate('300ms ease-out', style({
          opacity: 1,
          height: '*',
          transform: 'translateY(0)'
        }))
      ]),
      transition(':leave', [
        animate('300ms ease-in', style({
          opacity: 0,
          height: 0,
          transform: 'translateY(-10px)'
        }))
      ])
    ])
  ]
})
export class SidebarComponent implements OnInit {

  // ========== INPUTS/OUTPUTS (para compatibilidad con el layout existente) ==========

  @Input() showToggle = true;
  @Input() isCollapsed = false;
  @Output() toggleMobileNav = new EventEmitter<void>();
  @Output() toggleCollapsed = new EventEmitter<void>();

  // ========== ESTADO DEL SIDEBAR ==========

  /** Controla si el sidebar está visible en mobile (overlay) */

  isMobileMenuOpen = false;

  /** Detecta si estamos en vista mobile */
  isMobile = false;

  /** Ruta activa actual */
  activeRoute = '';

  // ========== ESTRUCTURA DEL MENÚ ==========

  /**
   * Menú de navegación desde sidebar-data.ts
   */
  // Fuente original del menú. No se pinta directamente: sirve como base para recalcular permisos.
  private readonly sourceMenuItems: NavItem[] = navItems;

  // IMPORTANTE: el HTML actual pinta `menuItems`, por eso aquí guardamos SOLO el menú ya filtrado.
  menuItems: NavItem[] = [];

  private parametroCtx = inject(ParametroGlobalContextService);
  readonly sessionService = inject(SessionService);
  private permissionService = inject(PermissionService);

  constructor(public router: Router) {
    // Recalcula el menú cuando cambian usuario, permisos o el parámetro de horas extras.
    effect(() => {
      this.sessionService.currentUser();
      this.permissionService.loaded();
      const permiteHe = this.parametroCtx.permiteHorasExtra();

      // Mantiene la regla de negocio existente para Horas Extras.
      const trabajador = this.sourceMenuItems.find(i => i.displayName === 'Trabajador');
      const horasExtras = trabajador?.children?.find(c => c.route === '/trabajadores/horas-extras');
      if (horasExtras) {
        horasExtras.hidden = !permiteHe;
      }

      // INC_HCM_56: el menú visible queda filtrado antes de llegar al HTML.
      this.menuItems = this.filterNavItems(this.sourceMenuItems);
    });
  }

  // ========== MENÚ FILTRADO POR PERMISOS ==========

  private filterNavItems(items: NavItem[]): NavItem[] {
    const filtered = items
      .filter(item => this.isItemVisible(item))
      .map(item => {
        const children = item.children
          ? this.filterChildren(item.children)
          : undefined;

        return {
          ...item,
          children,
        };
      })
      .filter(item => {
        // Los encabezados (INICIO, ADMINISTRACIÓN, MÓDULOS) se mantienen.
        if (item.navCap) return true;

        // Si un módulo tenía hijos y ninguno quedó autorizado, ocultar también el padre.
        if (item.children !== undefined && item.children.length === 0) {
          return false;
        }

        return true;
      });

    // Oculta encabezados que no tienen ninguna opción visible hasta el siguiente encabezado.
    return filtered.filter((item, index) => {
      if (!item.navCap) return true;
      const nextBoundary = filtered.slice(index + 1).find(next => next.navCap || next.displayName);
      return !!nextBoundary?.displayName;
    });
  }

  private filterChildren(children: NavItem[]): NavItem[] {
    return children
      .filter(child => {
        if (child.hidden) return false;

        if (child.roles?.length && !this.sessionService.hasAnyRole(child.roles)) {
          return false;
        }

        // INC_HCM_56: si el hijo declara permiso granular, solo aparece con VER o VER_PROPIO.
        if (child.permission) {
          return this.permissionService.tieneAccesoSubModulo(
            child.permission.modulo,
            child.permission.subModulo,
          );
        }

        // Compatibilidad: opciones antiguas sin permiso granular conservan el comportamiento previo.
        return true;
      })
      .map(child => ({
        ...child,
        children: child.children
          ? this.filterChildren(child.children)
          : undefined,
      }))
      .filter(child => {
        // Un contenedor interno sin ruta y sin hijos autorizados también desaparece.
        if (
          child.children !== undefined &&
          child.children.length === 0 &&
          !child.route
        ) {
          return false;
        }

        return true;
      });
  }

  private isItemVisible(item: NavItem): boolean {
  if (item.navCap) return true;
  if (item.hidden) return false;

  if (item.roles?.length) {
    if (!this.sessionService.hasAnyRole(item.roles)) return false;
  }

  if (item.permiso) {
    const tiene = this.permissionService.tieneAccesoModulo(item.permiso);
    return tiene;
  }

  // Sin permiso declarado
  if (item.route === '/dashboard') return true;
  return this.permissionService.esSuperAdmin();
}

  // ========== USUARIO ACTUAL ==========

  readonly currentUserSignal = this.sessionService.currentUser;

  get displayName(): string {
    const u = this.currentUserSignal();
    if (!u) return 'Usuario';
    if (u.trabajador) {
      const t = u.trabajador;
      return [t.nombre, t.apePaterno].filter(Boolean).join(' ').trim();
    }
    return u.username ?? 'Usuario';
  }

  readonly showMiPerfil = computed(() =>
    this.sessionService.hasAnyRole([...MI_PERFIL_ACCESS_ROLES]),
  );

  goMiPerfil(event?: Event): void {
    event?.preventDefault();
    if (!this.showMiPerfil()) return;
    void this.router.navigate(['/mi-perfil']);
  }

  get displayRole(): string {
    const u = this.currentUserSignal();
    if (!u || !u.roles || u.roles.length === 0) return '';

    // Orden de prioridad de roles para mostrar

    const priorityOrder = [
      UserRole.SUPER_ADMIN,
      UserRole.ADMIN,
      UserRole.TRABAJADOR,
      UserRole.GRH,
    ];

    // Buscar el rol de mayor prioridad que tenga el usuario

    for (const role of priorityOrder) {
      if (u.roles.includes(role)) {
        return role;
      }
    }

    // Si no coincide con ninguno de los conocidos, mostrar el primero

    return u.roles[0];
  }

  ngOnInit(): void {
  this.router.events
    .pipe(filter(event => event instanceof NavigationEnd))
    .subscribe((event: any) => {
      this.activeRoute = event.url;
      this.expandActiveMenu(); // ← agregar
      if (this.isMobile) {
        this.isMobileMenuOpen = false;
      }
    });

  this.activeRoute = this.router.url;
  this.expandActiveMenu(); // ← agregar
  this.checkScreenSize();
}

private expandActiveMenu(): void {
  this.menuItems.forEach(item => {
    if (item.children) {
      const hasActive = item.children.some(child =>
        this.isRouteActive(child.route)
      );
      if (hasActive) {
        item.isExpanded = true;
      }
    }
  });
}

  // ========== MÉTODOS DE CONTROL ==========

  toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
    this.toggleCollapsed.emit();

    // Si está colapsado, cerrar todos los submenús
    if (this.isCollapsed) {
      this.closeAllSubmenus();
    }
  }

  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    this.toggleMobileNav.emit();
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
    this.toggleMobileNav.emit();
  }

  toggleSubmenu(item: NavItem): void {
    if (this.isCollapsed && !this.isMobile) {
      return;
    }
    item.isExpanded = !item.isExpanded;

    // Cerrar otros submenús (accordion behavior)

    // Accordion: cierra otros submenús del mismo nivel
    this.menuItems.forEach(menuItem => {
      if (menuItem !== item && menuItem.children) {
        menuItem.isExpanded = false;
      }
    });
  }

  private closeAllSubmenus(): void {
    this.menuItems.forEach(item => {
      if (item.children) {
        item.isExpanded = false;
      }
    });
  }

  // ========== RUTAS ACTIVAS ==========

  isRouteActive(route?: string): boolean {
    if (!route) return false;
    const normalizedActive = this.normalizeRoute(this.activeRoute);
    const normalizedRoute = this.normalizeRoute(route);

    if (normalizedRoute === '/dashboard') {
      return normalizedActive === '/dashboard';
    }

    if (normalizedActive === normalizedRoute) {
      return true;
    }

    const routePrefix = normalizedRoute.endsWith('/') ? normalizedRoute : `${normalizedRoute}/`;
    return normalizedActive.startsWith(routePrefix);
  }

  private normalizeRoute(route: string): string {
    const cleaned = route.split('?')[0]?.split('#')[0]?.trim() ?? '';
    if (!cleaned) return '';
    const decoded = this.safeDecodeRoute(cleaned);
    return decoded.endsWith('/') && decoded.length > 1 ? decoded.slice(0, -1) : decoded;
  }

  private safeDecodeRoute(route: string): string {
    try {
      return decodeURIComponent(route);
    } catch {
      return route;
    }
  }

  hasActiveChild(item: NavItem): boolean {
    if (!item.children) return false;
    return item.children.some(child => this.isRouteActive(child.route));
  }

  // ========== RESPONSIVE ==========

  @HostListener('window:resize')
  onResize(): void {
    this.checkScreenSize();
  }

  private checkScreenSize(): void {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth < 768;

    if (wasMobile && !this.isMobile) {
      this.isMobileMenuOpen = false;
    }

    if (this.isMobile && this.isMobileMenuOpen) {
      this.isCollapsed = false;
    }
  }

  // ========== TRACKING ==========

  trackByLabel(index: number, item: NavItem): string {
  return item.route ??
         item.displayName ??
         item.navCap ??
         index.toString();
}
}
